import { ChangeEvent, useEffect, useState } from "react";

import useAcquireToken from "../../AuthToken/Hooks/useAcquireToken";
import type { RoleDTO } from "../../../apiclients/bstinsights";
import useBSTInsightsAPI from "../../../apiclients/api/bstinsightsapi";
import { isValidEmail } from "../../../utils/validations";
import { CreateUserOptions, useCreateUser } from './useCreateUser';

let timeout: NodeJS.Timeout;
/**
 *
 * Returns necessary props to load Add User Hyperfocus
 *
 * @returns
 * ```
 * {
 *    handleCreatButton,
 *    disabledButton,
 *    handleSubmitButton,
 *    handleUsername,
 *    handleEmail,
 *    handleUserRoles,
 *    handleResetForm
 *    createUser
 *    disabledButton,
 *    username,
 *    email,
 *    userRoles,
 *    dataUserRoles,
 *  }
 *```
 */
const useAddUser = () => {
  const [disabledButton, setDisabledButton] = useState<boolean>(true);
  const [isSendInvitationEnabled, setIsSendInvitationEnabled] =
    useState<boolean>(true);
  const [username, setUsername] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [userRoles, setUserRoles] = useState<Array<number>>([]);
  const [dataUserRoles, setDataUserRoles] = useState<Array<Required<RoleDTO>>>([]);
  const { getRoles, userExists } = useBSTInsightsAPI();
  const { getTokenSilently } = useAcquireToken();

  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const { mutate: createUser, isPending } = useCreateUser();

  useEffect(() => {
    loadDataRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUsername = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handleEmail = async (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailExists(null);
    clearTimeout(timeout);
    if (isValidEmail(e.target.value)) {
      timeout = setTimeout(async () => {
        const token: string = await getTokenSilently();
        const result = await userExists(token, e.target.value);
        setEmailExists(result);
      }, 1000);
    }
  };

  const handleUserRoles = (roleId: number) => {
    setUserRoles((prevState) => {
      if (prevState.includes(roleId)) {
        return prevState.filter((id) => id !== roleId)
      }
      return [
        ...prevState,
        roleId
      ]
    });
  };

  const handleSubmitButton = (sendInvitation: boolean, options?: CreateUserOptions) => {
    if (isPending) {
      return;
    }
    createUser(
      {
        email,
        roleIds: userRoles,
        displayName: username,
        sendInvitation
      },
      options
    );
  };

  const handleResetForm = () => {
    setUsername("");
    setEmail("");
    setUserRoles([]);
  };

  useEffect(() => {
    setDisabledButton(
      !(
        username?.trim() !== "" &&
        email?.trim() !== "" &&
        isValidEmail(email) &&
        emailExists === false
      )
    );
  }, [username, email, emailExists]);

  useEffect(() => {
    setIsSendInvitationEnabled(userRoles && userRoles.length > 0);
  }, [userRoles]);

  const loadDataRoles = async () => {
    const token: string = await getTokenSilently();
    const resultRoles = (await getRoles(token) || []) as Required<RoleDTO>[];
    setDataUserRoles(resultRoles);
  };

  return {
    handleSubmitButton,
    handleUsername,
    handleEmail,
    handleUserRoles,
    handleResetForm,
    disabledButton,
    username,
    email,
    emailExists,
    userRoles,
    dataUserRoles,
    isSendInvitationEnabled,
    isPending
  };
};

export default useAddUser;
